/**
 * GhostPositionStrategy
 *
 * E018-SOLID: Calculates positions for ghost nodes on canvas.
 *
 * Extracted from GhostSyncService to follow Single Responsibility Principle.
 * This class ONLY handles position calculation logic.
 */

import {
  GHOST_GAP,
  GHOST_HEIGHT,
  GHOST_STACK_OFFSET,
  GHOST_WIDTH,
} from '../core/constants';
import type {
  IGhostPositionStrategy,
  PositionContext,
  PositionResult,
} from '../core/interfaces/IGhostPositionStrategy';
import type { Position } from '../core/interfaces/IGhostPropertyMapper';
import type { GhostDefinition } from '../core/types';

// =============================================================================
// Implementation
// =============================================================================

/**
 * Calculates positions for ghost nodes.
 *
 * Position scenarios:
 * 1. New ghost: Position relative to source block (left or right side)
 * 2. Trashed target: Position at original location of deleted element
 * 3. Multiple ghosts: Stack vertically to avoid overlap
 */
export class GhostPositionStrategy implements IGhostPositionStrategy {
  readonly defaultWidth = GHOST_WIDTH;
  readonly defaultHeight = GHOST_HEIGHT;
  readonly gap = GHOST_GAP;

  private readonly DEFAULT_X = 100;
  private readonly DEFAULT_Y = 100;
  private readonly STACK_OFFSET = GHOST_STACK_OFFSET;

  /**
   * Calculates position for a ghost.
   */
  calculate(
    definition: GhostDefinition,
    context: PositionContext
  ): PositionResult {
    // Priority 1: Use original position if available (trashed element)
    if (definition.originalPosition) {
      const position = this.calculateFromOriginalPosition(
        definition.originalPosition
      );
      return {
        position,
        fromOriginalPosition: true,
        isFallback: false,
      };
    }

    // Priority 2: Calculate relative to source block
    const sourceBound = context.getBound(definition.sourceElementId);

    // Count existing ghosts from same source for vertical stacking
    const stackIndex = this.countGhostsFromSource(
      definition.sourceBlockId,
      context.existingGhosts
    );

    if (!sourceBound) {
      // Fallback: Use default position
      const position = this.calculateFallbackPosition(
        definition.preferredSide,
        stackIndex
      );
      return {
        position,
        fromOriginalPosition: false,
        isFallback: true,
      };
    }

    // Normal case: Position relative to source
    const position = this.calculateRelativePosition(
      sourceBound,
      definition.preferredSide,
      stackIndex
    );

    return {
      position,
      fromOriginalPosition: false,
      isFallback: false,
    };
  }

  /**
   * Calculates new position when a ghost's target moves to trash.
   */
  forTrashTransition(definition: GhostDefinition): Position | null {
    if (!definition.originalPosition) {
      return null;
    }
    return this.calculateFromOriginalPosition(definition.originalPosition);
  }

  /**
   * Calculates position from original element position.
   * Centers the ghost on where the original element was.
   */
  private calculateFromOriginalPosition(original: {
    x: number;
    y: number;
    w: number;
    h: number;
  }): Position {
    return {
      x: original.x + original.w / 2 - this.defaultWidth / 2,
      y: original.y + original.h / 2 - this.defaultHeight / 2,
    };
  }

  /**
   * Calculates position relative to source block.
   */
  private calculateRelativePosition(
    sourceBound: { x: number; y: number; w: number; h: number },
    preferredSide: 'left' | 'right',
    stackIndex: number
  ): Position {
    // Validate bound values
    const sx = Number.isFinite(sourceBound.x) ? sourceBound.x : this.DEFAULT_X;
    const sy = Number.isFinite(sourceBound.y) ? sourceBound.y : this.DEFAULT_Y;
    const sw = Number.isFinite(sourceBound.w) ? sourceBound.w : 200;
    const sh = Number.isFinite(sourceBound.h) ? sourceBound.h : 200;

    const yOffset = stackIndex * this.STACK_OFFSET;

    const x =
      preferredSide === 'right'
        ? sx + sw + this.gap
        : sx - this.defaultWidth - this.gap;

    const y = sy + sh / 2 - this.defaultHeight / 2 + yOffset;

    return {
      x: Number.isFinite(x) ? x : this.DEFAULT_X,
      y: Number.isFinite(y) ? y : this.DEFAULT_Y,
    };
  }

  /**
   * Calculates fallback position when source block is not found.
   */
  private calculateFallbackPosition(
    preferredSide: 'left' | 'right',
    stackIndex: number
  ): Position {
    const yOffset = stackIndex * this.STACK_OFFSET;
    return {
      x: this.DEFAULT_X + (preferredSide === 'left' ? 0 : 400),
      y: this.DEFAULT_Y + yOffset,
    };
  }

  /**
   * Counts how many ghosts already exist from the same source block.
   * Used for vertical stacking to avoid overlap.
   */
  private countGhostsFromSource(
    sourceBlockId: string,
    existingGhosts: ReadonlyMap<string, string>
  ): number {
    let count = 0;
    for (const key of existingGhosts.keys()) {
      if (key.startsWith(sourceBlockId)) {
        count++;
      }
    }
    return count;
  }
}
